import { Component, Inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import {
  MatDialog,
  MatDialogRef,
  MatDialogActions,
  MatDialogTitle,
  MatDialogContent,
  MatDialogModule,
  MAT_DIALOG_DATA
} from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule } from '@angular/material/core';
import { TablerIconsModule } from 'angular-tabler-icons';
import { FinalizationSubscriptionPlan } from '../../../../core/services/subscription-grouping-rules';

export interface ConfirmationFinalizingInvoiceData {
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  confirmColor?: 'primary' | 'accent' | 'warn';
  cancelColor?: 'primary' | 'accent' | 'warn';
  invoiceDate?: Date;
  minDate?: Date;
  maxDate?: Date;
  showDatePicker?: boolean;
  pennylaneCountryLabel?: string;
  /** The Pennylane subscriptions the finalization creates. Rendered as text, never HTML. */
  subscriptionPlan?: FinalizationSubscriptionPlan | null;
}

export interface InvoiceFinalizationResult {
  confirmed: boolean;
  selectedDate?: Date;
}

@Component({
  selector: 'app-confirmation-finalizing-invoice',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatButtonModule,
    MatDialogActions,
    MatDialogTitle,
    MatDialogContent,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    MatDatepickerModule,
    MatNativeDateModule,
    TablerIconsModule
  ],
  template: `
    <div class="dialog-container">
      <h5 mat-dialog-title class="dialog-title">{{ data.title }}</h5>
      
      <div mat-dialog-content>
        <div class="pennylane-current-target">
          <span class="pennylane-target-label">Pennylane target:</span>
          <span class="pennylane-target-value">Pennylane {{ data.pennylaneCountryLabel || 'Current workspace' }}</span>
        </div>

        <div class="message-card">
          <div [innerHTML]="data.message"></div>
        </div>

        <!-- What the finalization creates on Pennylane. Fee names come from the database:
             text interpolation only. -->
        <section class="sub-plan" *ngIf="data.subscriptionPlan as plan" data-testid="finalization-subscription-notice">
          <header class="sub-plan__head">
            <span class="sub-plan__icon"><i-tabler name="repeat" class="icon-16 d-flex"></i-tabler></span>
            <div>
              <div class="sub-plan__eyebrow">On finalization</div>
              <div class="sub-plan__heading">{{ plan.heading }}</div>
            </div>
          </header>

          <article class="sub-card" *ngFor="let sub of plan.subscriptions" [class.sub-card--shared]="sub.shared">
            <div class="sub-card__top">
              <span class="sub-card__title">{{ sub.title }}</span>
              <span class="sub-card__badge" *ngIf="sub.shared">
                <i-tabler name="stack-2" class="icon-12 d-flex"></i-tabler>
                Shared · {{ sub.lines.length }} fees
              </span>
            </div>
            <div class="sub-card__total">{{ sub.total }}</div>
            <div class="sub-card__period" *ngIf="sub.period">
              <i-tabler name="calendar-event" class="icon-12 d-flex"></i-tabler>
              {{ sub.period }}
            </div>
            <ul class="sub-card__lines" *ngIf="sub.shared">
              <li *ngFor="let line of sub.lines">
                <span>{{ line.name }}</span>
                <span class="sub-card__amount">{{ line.amount }}</span>
              </li>
            </ul>
          </article>

          <p class="sub-plan__note" *ngFor="let note of plan.notes">
            <i-tabler name="info-circle" class="icon-14 d-flex"></i-tabler>
            <span>{{ note }}</span>
          </p>
          <p class="sub-plan__hint">Fees with the same start date, end date and frequency share one subscription, one invoice line per fee.</p>
        </section>

        <div class="date-picker-section" *ngIf="data.showDatePicker">
          <mat-form-field appearance="outline" class="w-100">
            <mat-label>Select a new date</mat-label>
            <input 
              matInput 
              [matDatepicker]="picker"
              [(ngModel)]="selectedDate"
              [min]="data.minDate"
              [max]="data.maxDate"
              (dateChange)="onDateChange($event)"
              required>
            <mat-datepicker-toggle matSuffix [for]="picker"></mat-datepicker-toggle>
            <mat-datepicker #picker></mat-datepicker>
          </mat-form-field>
          
          <div class="error-message" *ngIf="errorMessage">
            {{ errorMessage }}
          </div>
        </div>
      </div>
      
      <div mat-dialog-actions class="dialog-actions">
        <button 
          mat-flat-button 
          [color]="data.cancelColor || 'warn'"
          class="bg-error text-white"
          (click)="onCancel()">
          {{ data.cancelText || 'Cancel' }}
        </button>
        <button 
          mat-flat-button 
          [color]="data.confirmColor || 'primary'"
          class="bg-primary text-white"
          [disabled]="data.showDatePicker && !isValid"
          (click)="onConfirm()"
          cdkFocusInitial>
          {{ data.confirmText || 'Confirm' }}
        </button>
      </div>
    </div>
  `,
  styles: [`
    .dialog-container {
      padding: 24px;
      text-align: center;
      background: var(--bg-color, #ffffff);
      color: var(--text-color, #333333);
      min-width: 400px;
    }

    .dialog-title {
      font-size: 24px;
      font-weight: 600;
      margin-bottom: 16px;
      color: var(--text-color, #333333);
    }

    .message-card {
      background: var(--card-bg, #ffffff);
      border: 1px solid var(--border-color, #e0e0e0);
      border-radius: 8px;
      padding: 16px;
      margin-bottom: 20px;
      font-size: 14px;
      color: var(--text-secondary, #666666);
      text-align: left;
    }

    .pennylane-current-target {
      text-align: left;
      margin-bottom: 14px;
      display: flex;
      align-items: center;
      gap: 8px;
      flex-wrap: wrap;
    }

    .pennylane-target-label {
      font-size: 13px;
      color: var(--text-secondary, #666666);
      font-weight: 500;
    }

    .pennylane-target-value {
      display: inline-block;
      font-size: 13px;
      font-weight: 600;
      color: #5D87FF;
      background: rgba(93, 135, 255, 0.12);
      border: 1px solid rgba(93, 135, 255, 0.35);
      border-radius: 999px;
      padding: 4px 10px;
    }

    .message-card p {
      margin: 8px 0;
    }

    /* Subscription plan: design tokens only (light and dark are defined there). */
    .sub-plan {
      text-align: left;
      border: 1px solid var(--ap-color-border);
      border-radius: var(--ap-radius);
      background: var(--ap-color-surface);
      padding: 14px;
      margin-bottom: 20px;
    }

    .sub-plan__head {
      display: flex;
      align-items: center;
      gap: 10px;
      margin-bottom: 12px;
    }

    .sub-plan__icon {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 32px;
      height: 32px;
      border-radius: 50%;
      background: var(--ap-color-primary-light);
      color: var(--ap-color-primary);
      flex-shrink: 0;
    }

    .sub-plan__eyebrow {
      font-size: 11px;
      font-weight: 600;
      letter-spacing: .04em;
      text-transform: uppercase;
      color: var(--ap-color-text-muted);
    }

    .sub-plan__heading {
      font-size: 15px;
      font-weight: 600;
      color: var(--ap-color-text);
    }

    .sub-card {
      border-left: 3px solid var(--ap-color-primary);
      background: var(--ap-color-primary-light);
      border-radius: 8px;
      padding: 10px 12px;
      margin-bottom: 8px;
    }

    .sub-card__top {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 8px;
    }

    .sub-card__title {
      font-size: 13px;
      font-weight: 600;
      color: var(--ap-color-text);
    }

    .sub-card__badge {
      display: inline-flex;
      align-items: center;
      gap: 4px;
      font-size: 11px;
      font-weight: 600;
      padding: 2px 8px;
      border-radius: var(--ap-radius-pill);
      background: var(--ap-color-surface);
      color: var(--ap-color-primary);
      white-space: nowrap;
    }

    .sub-card__total {
      font-size: 20px;
      font-weight: 700;
      color: var(--ap-color-primary);
      margin-top: 4px;
    }

    .sub-card__period {
      display: flex;
      align-items: center;
      gap: 4px;
      font-size: 12px;
      color: var(--ap-color-text-muted);
    }

    .sub-card__lines {
      list-style: none;
      margin: 8px 0 0;
      padding: 8px 0 0;
      border-top: 1px dashed var(--ap-color-border);
      font-size: 12px;
      color: var(--ap-color-text);
    }

    .sub-card__lines li {
      display: flex;
      justify-content: space-between;
      gap: 12px;
      padding: 2px 0;
    }

    .sub-card__amount {
      font-weight: 600;
      white-space: nowrap;
    }

    .sub-plan__note {
      display: flex;
      gap: 6px;
      align-items: flex-start;
      font-size: 12px;
      margin: 6px 0 0;
      padding: 6px 8px;
      border-radius: 8px;
      background: var(--ap-color-warning-light);
      color: var(--ap-color-warning-ink);
    }

    .sub-plan__hint {
      font-size: 11px;
      margin: 8px 0 0;
      color: var(--ap-color-text-muted);
    }

    .date-picker-section {
      margin-top: 20px;
      text-align: left;
    }

    .w-100 {
      width: 100%;
    }

    ::ng-deep .mat-mdc-form-field {
      width: 100%;
    }

    ::ng-deep .mat-mdc-text-field-wrapper {
      background: var(--input-bg, #ffffff);
    }

    .error-message {
      color: #ff6692;
      font-size: 12px;
      margin-top: 8px;
      font-weight: 500;
    }

    .dialog-actions {
      display: flex;
      justify-content: center;
      gap: 12px;
      margin-top: 24px;
      padding: 0;
    }

    .bg-error {
      background-color: #ff6692 !important;
      color: white !important;
      border-radius: 8px;
      padding: 10px 24px;
      font-weight: 500;
      border: none;
      cursor: pointer;
      transition: background-color 0.3s ease;
    }

    .bg-error:hover {
      background-color: #ff6692 !important;
    }

    .bg-primary {
      background-color: #5D87FF !important;
      color: white !important;
      border-radius: 8px;
      padding: 10px 24px;
      font-weight: 500;
      border: none;
      cursor: pointer;
      transition: background-color 0.3s ease;
    }

    .bg-primary:hover {
      background-color: #4a6fd5 !important;
    }

    .bg-primary:disabled {
      background-color: #cccccc !important;
      cursor: not-allowed;
      opacity: 0.6;
    }

    /* Mode nuit */
    :host-context(.dark-theme) .dialog-container {
      --bg-color: #1a2537;
      --text-color: #ffffff;
      --card-bg: #1a2537;
      --border-color: #404040;
      --text-secondary: #cccccc;
      --input-bg: #1a2537;
    }

    :host-context(.dark-theme) .dialog-title {
      color: #ffffff;
    }

    :host-context(.dark-theme) .message-card {
      background: #1a2537;
      border-color: #404040;
      color: #cccccc;
    }

    :host-context(.dark-theme) ::ng-deep .mat-mdc-text-field-wrapper {
      background: #1a2537;
    }

    :host-context(.dark-theme) ::ng-deep .mat-mdc-form-field {
      color: #ffffff;
    }

    :host-context(.dark-theme) ::ng-deep .mat-mdc-input-element {
      color: #ffffff;
    }

    :host-context(.dark-theme) ::ng-deep .mdc-text-field--outlined .mdc-notched-outline__leading,
    :host-context(.dark-theme) ::ng-deep .mdc-text-field--outlined .mdc-notched-outline__notch,
    :host-context(.dark-theme) ::ng-deep .mdc-text-field--outlined .mdc-notched-outline__trailing {
      border-color: #404040;
    }

    :host-context(.dark-theme) ::ng-deep .mat-mdc-form-field-focus-overlay {
      background-color: transparent;
    }
  `]
})
export class ConfirmationFinalizingInvoiceComponent {
  selectedDate: Date | null = null;
  errorMessage: string = '';
  isValid: boolean = false;

  constructor(
    public dialogRef: MatDialogRef<ConfirmationFinalizingInvoiceComponent>,
    @Inject(MAT_DIALOG_DATA) public data: ConfirmationFinalizingInvoiceData
  ) {
    // Initialiser avec la date fournie ou la date min
    if (this.data.invoiceDate) {
      this.selectedDate = this.data.invoiceDate;
      this.isValid = true;
    } else if (this.data.minDate) {
      this.selectedDate = this.data.minDate;
      this.isValid = true;
    }
  }

  onDateChange(event: any): void {
    if (event.value) {
      this.selectedDate = event.value;
      this.validateDate();
    }
  }

  validateDate(): void {
    if (!this.selectedDate) {
      this.errorMessage = 'Please select a date';
      this.isValid = false;
      return;
    }

    if (this.data.minDate && this.selectedDate < this.data.minDate) {
      this.errorMessage = 'Date cannot be before the minimum date';
      this.isValid = false;
      return;
    }

    if (this.data.maxDate && this.selectedDate > this.data.maxDate) {
      this.errorMessage = 'Date cannot be after the maximum date';
      this.isValid = false;
      return;
    }

    this.errorMessage = '';
    this.isValid = true;
  }

  onConfirm(): void {
    if (this.data.showDatePicker) {
      this.validateDate();
      if (!this.isValid) {
        return;
      }
    }

    const result: InvoiceFinalizationResult = {
      confirmed: true,
      selectedDate: this.selectedDate || undefined
    };
    this.dialogRef.close(result);
  }

  onCancel(): void {
    const result: InvoiceFinalizationResult = {
      confirmed: false
    };
    this.dialogRef.close(result);
  }
}
